// components/expenses/DeletedExpensesTable.jsx
import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    CircularProgress,
    Stack,
    Box
} from '@mui/material';
import {
    RestoreFromTrash,
    DeleteForever,
    Info as InfoIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ExpenseAuditDialog from './ExpenseAuditDialog';

const DeletedExpensesTable = ({
    expenses,
    loading,
    user,
    onRestore,
    onPermanentDelete,
    canRestore,
    canPermanentDelete,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange
}) => {
    const { t } = useTranslation();
    const [auditExpense, setAuditExpense] = useState(null);
    const [auditDialogOpen, setAuditDialogOpen] = useState(false);

    const handleOpenAudit = (expense) => {
        setAuditExpense(expense);
        setAuditDialogOpen(true);
    };

    const handleCloseAudit = () => {
        setAuditDialogOpen(false);
        setAuditExpense(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'rejected': return 'error';
            case 'paid': return 'info';
            case 'pending':
            default: return 'warning';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'approved': return t('expenses.approved', 'Approved');
            case 'rejected': return t('expenses.rejected', 'Rejected');
            case 'paid': return t('expenses.paid', 'Paid');
            case 'pending':
            default: return t('expenses.pending', 'Pending');
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return format(new Date(date), 'dd/MM/yyyy');
    };

    const formatDateTime = (date) => {
        if (!date) return '-';
        return format(new Date(date), 'dd/MM/yyyy HH:mm');
    };

    return (
        <>
            <Paper sx={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(244, 67, 54, 0.04)' }}>
                                <TableCell>{t('expenses.description', 'Description')}</TableCell>
                                <TableCell align="right">{t('expenses.amount', 'Amount')}</TableCell>
                                <TableCell>{t('common.category', 'Category')}</TableCell>
                                <TableCell>{t('expenses.date', 'Date')}</TableCell>
                                <TableCell>{t('common.branch', 'Branch')}</TableCell>
                                <TableCell>{t('expenses.statusBeforeDeletion', 'Previous Status')}</TableCell>
                                <TableCell>{t('expenses.deletedBy', 'Deleted By')}</TableCell>
                                <TableCell>{t('expenses.deletedAt', 'Deleted At')}</TableCell>
                                <TableCell>{t('expenses.deletionReason', 'Reason')}</TableCell>
                                <TableCell>{t('common.actions', 'Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : expenses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {t('expenses.noDeletedExpenses', 'No deleted expenses found')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                expenses.map((expense) => (
                                    <TableRow
                                        key={expense._id}
                                        hover
                                        sx={{
                                            backgroundColor: 'rgba(244, 67, 54, 0.02)',
                                            opacity: 0.85,
                                            '&:hover': {
                                                opacity: 1,
                                                backgroundColor: 'rgba(244, 67, 54, 0.06)',
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {expense.name}
                                            </Typography>
                                            {expense.notes && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {expense.notes}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                            >
                                                {expense.amount?.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {expense.category?.name ? (
                                                <Chip
                                                    label={expense.category.name}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>{formatDate(expense.date)}</TableCell>
                                        <TableCell>{expense.branch?.name || '-'}</TableCell>
                                        <TableCell align="center">
                                            {expense.statusBeforeDeletion ? (
                                                <Chip
                                                    label={getStatusLabel(expense.statusBeforeDeletion)}
                                                    color={getStatusColor(expense.statusBeforeDeletion)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {expense.deletedByUser?.name || expense.deletedByUser?.username || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDateTime(expense.deletedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {expense.deletionReason ? (
                                                <Tooltip title={expense.deletionReason}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                maxWidth: 120,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {expense.deletionReason}
                                                        </Typography>
                                                        <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('expenses.noReason', 'No reason given')}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                {/* Audit Trail */}
                                                <Tooltip title={t('expenses.audit.viewHistory', 'View History')}>
                                                    <IconButton
                                                        size="small"
                                                        color="default"
                                                        onClick={() => handleOpenAudit(expense)}
                                                    >
                                                        <HistoryIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {canRestore && (
                                                    <Tooltip title={t('expenses.restore', 'Restore')}>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => onRestore(expense)}
                                                        >
                                                            <RestoreFromTrash />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canPermanentDelete && (
                                                    <Tooltip title={t('expenses.permanentDelete', 'Permanently Delete')}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => onPermanentDelete(expense)}
                                                        >
                                                            <DeleteForever />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper sx={{ width: '100%', mt: 2 }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={onPageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={onRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                />
            </Paper>

            {/* Audit Dialog */}
            <ExpenseAuditDialog
                open={auditDialogOpen}
                onClose={handleCloseAudit}
                expense={auditExpense}
            />
        </>
    );
};

export default DeletedExpensesTable;